import moment from "moment/min/moment-with-locales";
import type { MonthlyStatementResult } from "@/services/api/business/getMonthlyStatement";

/**
 * Business statement maths — the web port of the RN
 * `computeMonthlyStatementTotals`.
 *
 * Business rules:
 * - the platform fee is a fixed amount, waived for the three full months of
 *   probation after the account was created and prorated in the month the
 *   probation ends;
 * - "Earnings" equals gross sales (the coupon original price actually used) —
 *   fees are listed separately and never deducted from it.
 */

const PLATFORM_FEE_FIXED = Math.round((500 / 12) * 100) / 100;
const PROBATION_MONTHS = 3;

export type BusinessStatementFeeLine = {
  code: string;
  amount: number;
  meta?: Record<string, any>;
};

export type BusinessStatementTotals = {
  currency: string;
  platformFee: number;
  commissionFee: number;
  totalFees: number;
  grossSales: number;
  earnings: number;
  fees: BusinessStatementFeeLine[];
  couponShortfallFee: number;
};

const toNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const buildFeeLines = (
  statement: MonthlyStatementResult | null | undefined,
  platformFee: number,
): BusinessStatementFeeLine[] => {
  const breakdown = statement?.fees_breakdown;
  if (Array.isArray(breakdown) && breakdown.length > 0) {
    return breakdown
      .filter((line) => line && typeof line.code === "string")
      .map((line) => ({
        code: line.code,
        amount: toNumber(line.amount) ?? 0,
        meta: line.meta,
      }));
  }

  // Backward compatibility for statements issued before the itemized
  // breakdown existed: synthesize the same two lines the backend would.
  const lines: BusinessStatementFeeLine[] = [
    {
      code: "platform_fee",
      amount: toNumber(statement?.platform_fee_total) ?? platformFee,
      meta: {},
    },
  ];
  const shortfall = toNumber(statement?.coupon_shortfall_fee);
  if (shortfall !== null) {
    lines.push({ code: "coupon_shortfall", amount: shortfall, meta: {} });
  }
  return lines;
};

export const formatMoney = (value: number, currency?: string) => {
  const safe = Number.isFinite(value) ? value : 0;
  const formatted = safe.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : formatted;
};

export const computeStatementTotals = (
  statement: MonthlyStatementResult | null | undefined,
  monthStart: moment.Moment,
  userCreatedAt?: string | number | null,
): BusinessStatementTotals => {
  const summary = statement?.summary;
  const currency = String(statement?.currency ?? "HKD");

  const commissionFee =
    toNumber(
      summary?.total_commission_fee,
      statement?.commission_fee_total,
    ) ?? 0;

  const grossSales =
    toNumber(
      summary?.total_original_price_used,
      summary?.total_price,
    ) ?? 0;

  // Platform fee: backend total wins, otherwise the fixed fee adjusted for the
  // probation period.
  let platformFee =
    toNumber(statement?.platform_fee_total) ?? PLATFORM_FEE_FIXED;
  const createdAt = normalizeCreatedAt(userCreatedAt);
  if (createdAt?.isValid()) {
    const probationEnd = createdAt.clone().add(PROBATION_MONTHS, "months");
    const monthEnd = monthStart.clone().endOf("month");
    if (monthEnd.isBefore(probationEnd, "day")) {
      platformFee = 0;
    } else if (monthStart.isSame(probationEnd, "month")) {
      const activeStart = moment.max(
        probationEnd.clone().startOf("day"),
        monthStart.clone(),
      );
      const remainingDays =
        monthEnd.clone().startOf("day").diff(activeStart, "days") + 1;
      platformFee =
        PLATFORM_FEE_FIXED *
        Math.max(0, Math.min(1, remainingDays / Math.max(1, monthStart.daysInMonth())));
    }
  }

  const totalFees =
    toNumber(statement?.total_fee) ?? platformFee + commissionFee;

  const fees = buildFeeLines(statement, platformFee);
  const couponShortfallFee = round2(
    toNumber(statement?.coupon_shortfall_fee) ??
      toNumber(fees.find((line) => line.code === "coupon_shortfall")?.amount) ??
      0,
  );

  return {
    currency,
    platformFee: round2(platformFee),
    commissionFee: round2(commissionFee),
    totalFees: round2(totalFees),
    grossSales: round2(grossSales),
    earnings: round2(grossSales),
    fees: fees.map((line) => ({ ...line, amount: round2(line.amount) })),
    couponShortfallFee,
  };
};

const round2 = (value: number) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * `business_profiles.ctime` reaches us either as an ISO string or as unix
 * seconds (the API stores timestamps both ways), so scale bare numbers.
 */
const normalizeCreatedAt = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return moment(value > 1e12 ? value : value * 1000);
  }
  if (/^\d+$/.test(value)) {
    const numeric = Number(value);
    return moment(numeric > 1e12 ? numeric : numeric * 1000);
  }
  return moment(value);
};
